/*
 * VenueMap — the map browser shared by Accommodation / Food / Transport / Flights.
 *
 * Uses MapLibre GL with OpenStreetMap raster tiles (no API key needed). Pins
 * come from POST /api/logistics/exercises/{ex_id}/venues/search on the current
 * bbox. Pans/zooms debounce a refetch. Selected venue emits `onSelect`.
 *
 * Categories at P1a: hotel | restaurant | transport | hospital
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { motion, AnimatePresence } from 'framer-motion';
import { MapPin, Star, WifiHigh, Car, ArrowsClockwise } from '@phosphor-icons/react';
import { useTheme } from '../../context/ThemeContext';
import logisticsApi from '../../lib/logisticsApi';

const OSM_STYLE_LIGHT = {
  version: 8,
  sources: {
    osm: {
      type: 'raster',
      tiles: ['https://a.tile.openstreetmap.org/{z}/{x}/{y}.png', 'https://b.tile.openstreetmap.org/{z}/{x}/{y}.png'],
      tileSize: 256,
      attribution: '© OpenStreetMap contributors',
    },
  },
  layers: [{ id: 'osm', type: 'raster', source: 'osm' }],
};
const OSM_STYLE_DARK = {
  version: 8,
  sources: {
    // Carto's dark tiles — free with attribution
    carto: {
      type: 'raster',
      tiles: [
        'https://a.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}.png',
        'https://b.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}.png',
        'https://c.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}.png',
      ],
      tileSize: 256,
      attribution: '© OpenStreetMap · © CARTO',
    },
  },
  layers: [{ id: 'carto', type: 'raster', source: 'carto' }],
};

const debounce = (fn, wait) => {
  let t; return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), wait); };
};

const VenueMap = ({
  exerciseId,
  category = 'hotel',
  center = [-0.13, 51.51],   // London default
  zoom = 13,
  onSelect,
  selectedVenueId,
  height = '540px',
  onMapClick,
  placeMode = false,
}) => {
  const { isDark } = useTheme();
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [cacheHit, setCacheHit] = useState(false);
  const [ready, setReady] = useState(false);
  const fetchRef = useRef(null);

  const fetchVenues = useCallback(async () => {
    const map = mapRef.current;
    console.log('[VenueMap] fetchVenues called', { exerciseId, category, hasMap: !!map });
    if (!map || !exerciseId) return;
    const b = map.getBounds();
    const bbox = [b.getSouth(), b.getWest(), b.getNorth(), b.getEast()];
    console.log('[VenueMap] bbox=', bbox);
    // Guard against overly-large bboxes (Overpass hates >~50k km²).
    const area = (bbox[2] - bbox[0]) * (bbox[3] - bbox[1]);
    if (area > 4) {
      setError('Zoom in a bit — the area is too large to search.');
      setVenues([]);
      return;
    }
    setLoading(true); setError(null);
    try {
      console.log('[VenueMap] issuing searchVenues to', exerciseId);
      const res = await logisticsApi.searchVenues(exerciseId, {
        bbox, category, limit: 250,
      });
      console.log('[VenueMap] searchVenues resolved with', res?.count, 'venues');
      setVenues(res.venues || []);
      setCacheHit(!!res.cache_hit);
    } catch (e) {
      if (e.name === 'AbortError') return;
      console.log('[VenueMap] searchVenues FAILED', e?.message || e);
      setError(e.message || 'Failed to load venues');
      setVenues([]);
    } finally { setLoading(false); }
  }, [exerciseId, category]);

  // Keep a live ref to the latest fetchVenues so listeners registered at
  // mount call the current closure (not a stale one).
  useEffect(() => { fetchRef.current = fetchVenues; }, [fetchVenues]);

  // Init map once
  useEffect(() => {
    if (mapRef.current) return;
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: isDark ? OSM_STYLE_DARK : OSM_STYLE_LIGHT,
      center,
      zoom,
      attributionControl: { compact: true },
    });
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right');
    mapRef.current = map;
    map.on('click', (e) => {
      if (onMapClick) onMapClick({ lat: e.lngLat.lat, lng: e.lngLat.lng });
    });
    const debounced = debounce(() => { setReady(true); fetchRef.current?.(); }, 400);
    map.on('moveend', debounced);
    // Belt-and-braces initial fetch: fire once after a small settle time.
    const timer = setTimeout(() => {
      console.log('[VenueMap] init timer fired, has fetchRef?', !!fetchRef.current);
      setReady(true);
      fetchRef.current?.();
    }, 900);
    return () => { clearTimeout(timer); map.remove(); mapRef.current = null; };
     
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Swap style on theme change
  useEffect(() => {
    if (!mapRef.current) return;
    mapRef.current.setStyle(isDark ? OSM_STYLE_DARK : OSM_STYLE_LIGHT);
  }, [isDark]);

  // Re-fetch when category changes (skip the very first render — that's
  // handled by the init timer inside the map-init effect).
  const firstRender = useRef(true);
  useEffect(() => {
    if (firstRender.current) { firstRender.current = false; return; }
    if (!ready) return;
    fetchVenues();
  }, [category, ready, fetchVenues]);

  // Render pins whenever `venues` changes
  useEffect(() => {
    // Clear old markers
    markersRef.current.forEach(m => m.remove());
    markersRef.current = [];
    const map = mapRef.current;
    if (!map) return;
    venues.forEach((v) => {
      const el = document.createElement('button');
      el.className = 'ls-map-pin';
      el.setAttribute('data-testid', `ls-map-pin-${v.venue_id}`);
      const priceStr = v.price_hint ? `£${(v.price_hint / 100).toFixed(0)}` : v.subcategory;
      const isSelected = selectedVenueId === v.venue_id;
      el.innerHTML = `
        <span class="ls-map-pin-inner ${isSelected ? 'ls-map-pin-selected' : ''}">
          ${priceStr}
        </span>
      `;
      el.addEventListener('click', (e) => {
        e.stopPropagation();
        onSelect?.(v);
        map.easeTo({ center: [v.lng, v.lat], zoom: Math.max(map.getZoom(), 14), duration: 400 });
      });
      const marker = new maplibregl.Marker({ element: el, anchor: 'bottom' })
        .setLngLat([v.lng, v.lat])
        .addTo(map);
      markersRef.current.push(marker);
    });
  }, [venues, selectedVenueId, onSelect]);

  return (
    <div className="relative w-full ls-surface overflow-hidden" style={{ height }}>
      <div ref={containerRef} className="w-full h-full" data-testid="ls-venue-map" />

      {/* HUD overlay — status pill + refresh */}
      <div className="absolute top-3 left-3 flex items-center gap-2 z-10">
        <div className="ls-glass rounded-full pl-3 pr-3 py-1.5 flex items-center gap-2 text-xs">
          {loading ? (
            <>
              <ArrowsClockwise size={12} weight="bold" className="animate-spin" style={{ color: 'var(--ls-primary)' }} />
              <span style={{ color: 'var(--ls-muted)' }}>Searching…</span>
            </>
          ) : error ? (
            <span style={{ color: 'var(--ls-danger)' }} data-testid="ls-map-error">{error}</span>
          ) : (
            <>
              <MapPin size={12} weight="fill" style={{ color: 'var(--ls-primary)' }} />
              <span className="ls-mono">{venues.length}</span>
              <span style={{ color: 'var(--ls-muted)' }}>
                {cacheHit ? 'cached' : 'live'}
              </span>
            </>
          )}
        </div>
        <button className="ls-btn-ghost text-xs !py-1.5 !px-3" onClick={fetchVenues} data-testid="ls-map-refresh">
          Search this area
        </button>
      </div>

      {/* Attribution shim so we match design tokens */}
      <style>{`
        .ls-map-pin {
          background: transparent; border: 0; padding: 0; cursor: pointer;
          transform: translateY(-6px);
        }
        .ls-map-pin-inner {
          display: inline-block;
          background: var(--ls-surface);
          color: var(--ls-secondary);
          border: 1px solid var(--ls-border);
          padding: 5px 9px;
          border-radius: 999px;
          font-family: var(--ls-font-mono);
          font-size: 11px;
          font-weight: 600;
          box-shadow: 0 2px 6px rgba(0,0,0,0.14);
          transition: transform 160ms var(--ls-ease), background 160ms var(--ls-ease), color 160ms var(--ls-ease);
        }
        .ls-map-pin:hover .ls-map-pin-inner {
          transform: scale(1.08);
          background: var(--ls-primary);
          color: var(--ls-primary-fg);
          border-color: var(--ls-primary);
        }
        .ls-map-pin-selected {
          background: var(--ls-primary) !important;
          color: var(--ls-primary-fg) !important;
          border-color: var(--ls-primary) !important;
        }
        .maplibregl-ctrl-attrib.maplibregl-compact {
          background: var(--ls-glass, rgba(255,255,255,0.8)) !important;
        }
      `}</style>
    </div>
  );
};

/** Venue detail card — right-panel companion to the map. */
export const VenueDetailCard = ({ venue, onBook, bookDisabledReason, onViewDetails, onEdit, editMode }) => {
  if (!venue) {
    return (
      <div className="ls-elevated ls-noise h-full flex flex-col items-center justify-center p-8 text-center">
        <MapPin size={48} weight="duotone" style={{ color: 'var(--ls-primary)' }} />
        <p className="ls-heading text-lg font-semibold mt-4">Pick a venue</p>
        <p className="text-sm mt-1" style={{ color: 'var(--ls-muted)' }}>
          Tap any pin on the map to see photos, features, and simulated pricing.
        </p>
      </div>
    );
  }
  const featIcon = { wifi: WifiHigh, parking: Car };
  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={venue.venue_id}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -6 }}
        transition={{ duration: 0.25 }}
        className="ls-surface overflow-hidden"
        data-testid={`ls-venue-detail-${venue.venue_id}`}
      >
        <div className="h-40 bg-cover bg-center" style={{ backgroundImage: `url(${venue.photo})`, background: `url(${venue.photo}) center / cover, var(--ls-elevated)` }} />
        <div className="p-5">
          <div className="flex items-start gap-2 mb-2">
            <span className="ls-overline">{venue.subcategory || venue.category}</span>
            {!!venue.stars && (
              <span className="flex items-center gap-0.5">
                {Array.from({ length: venue.stars }).map((_, i) => (
                  <Star key={i} size={10} weight="fill" style={{ color: 'var(--ls-primary)' }} />
                ))}
              </span>
            )}
            {venue.overridden && (
              <span className="text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider ml-auto"
                style={{ background: 'var(--ls-warning)', color: '#1a1408' }}>
                Overridden
              </span>
            )}
          </div>
          <h3 className="ls-heading text-xl font-bold leading-tight mb-1">{venue.name}</h3>
          {venue.address && (
            <p className="text-xs mb-4" style={{ color: 'var(--ls-muted)' }}>{venue.address}</p>
          )}
          {venue.features?.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-4">
              {venue.features.slice(0, 6).map((f) => {
                const Icon = featIcon[f];
                return (
                  <span key={f} className="text-[10px] px-2 py-1 rounded flex items-center gap-1"
                    style={{ background: 'var(--ls-elevated)', color: 'var(--ls-muted)' }}>
                    {Icon && <Icon size={10} weight="bold" />}
                    {f.replace(/_/g, ' ')}
                  </span>
                );
              })}
            </div>
          )}
          {/* Action buttons */}
          <div className="flex gap-2 mt-4 mb-3">
            <button
              className="ls-btn-primary flex-1"
              data-testid={`ls-venue-detail-btn-${venue.venue_id}`}
              onClick={() => onViewDetails?.(venue)}
            >
              View details
            </button>
            {editMode && (
              <button
                className="ls-btn-ghost"
                data-testid={`ls-venue-edit-btn-${venue.venue_id}`}
                onClick={() => onEdit?.(venue)}
                title="Edit venue"
              >
                Edit
              </button>
            )}
          </div>
          <div className="flex items-end justify-between">
            <div>
              <p className="ls-overline">From</p>
              <p className="ls-mono text-3xl font-bold" style={{ color: 'var(--ls-primary)' }}>
                £{(venue.price_hint / 100).toFixed(2)}
              </p>
              <p className="text-[10px]" style={{ color: 'var(--ls-muted)' }}>/ night · simulated</p>
            </div>
            <button
              className="ls-btn-primary"
              data-testid={`ls-venue-book-${venue.venue_id}`}
              onClick={() => onBook?.(venue)}
              disabled={!onBook || !!bookDisabledReason}
              title={bookDisabledReason || 'Book this stay'}
            >
              Book stay
            </button>
          </div>
          {bookDisabledReason && (
            <p className="text-[11px] mt-2 text-right" style={{ color: 'var(--ls-muted)' }}>
              {bookDisabledReason}
            </p>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default VenueMap;
